/**
 * InlineKanbanPickers
 *
 * Dois seletores inline usados na sidebar do chat para alterar a Categoria
 * e a Fase do Kanban de Conversas direto na tela do chat.
 *
 * Fluxo de duas etapas (v1.1.0):
 *   1) Selecionar uma categoria diferente da atual = APENAS estado local
 *      (preview). Nenhuma gravação no banco.
 *   2) Selecionar uma fase = persiste no banco via `moveConversation` —
 *      esta é a única operação que altera o backend.
 *
 * Estado compartilhado:
 *   - `pendingCategoryId` / `onPendingCategoryChange` são donos do parent
 *     (ex.: `ContactSidebar`), permitindo que o `InlinePhasePicker` mostre
 *     as fases da categoria pendente e que o pill da categoria sinalize
 *     a pendência.
 *
 * Permissão: reusa `usePermissions().canEdit('kanban')`.
 *
 * CHANGELOG:
 *   1.2.0 — Corrigido bug de scroll ausente nos popovers de categoria/fase
 *           quando o tenant possui muitas fases (10+). Substituído `ScrollArea`
 *           (Radix) por div nativo com `overflow-y-auto` porque `max-h-*`
 *           isolado não permite ao Viewport `h-full` do Radix resolver altura,
 *           ocultando itens sem exibir a barra de rolagem.
 *   1.1.0 — Fluxo de duas etapas (categoria pendente + confirmação de fase).
 *
 * Versão: 1.2.0
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Tag, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useConversationKanbanOptions,
  type CategoryWithPhases,
} from '@/hooks/useConversationKanbanOptions';
import { useKanban } from '@/hooks/useKanban';
import { usePermissions } from '@/hooks/usePermissions';
import { createLogger } from '@/lib/logger';

const log = createLogger('InlineKanbanPickers');

/** Props comuns aos dois pickers */
interface CommonProps {
  conversationId: string;
  contactId: string;
  inboxId: string;
  /** Fase atual da conversa (necessária para extrair categoria e destacar seleção) */
  currentPhase: {
    id: string;
    name: string;
    color: string;
    category?: {
      id: string;
      name: string;
      color: string;
    } | null;
  } | null | undefined;
  /** Categoria escolhida localmente (preview), ainda não persistida */
  pendingCategoryId: string | null;
  /** Atualiza a categoria pendente (null = cancela preview) */
  onPendingCategoryChange: (categoryId: string | null) => void;
  /** Disparado após sucesso para refrescar a UI do chat */
  onChanged?: () => void;
}

// ---------------------------------------------------------------------------
// Item da lista do popover (com bolinha de cor + check de seleção)
// ---------------------------------------------------------------------------
function PopoverListItem({
  color,
  label,
  isSelected,
  onClick,
  hint,
}: {
  color: string;
  label: string;
  isSelected: boolean;
  onClick: () => void;
  hint?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left text-sm',
        'transition-colors hover:bg-muted cursor-pointer',
        isSelected && 'bg-muted',
      )}
      title={hint}
    >
      <span
        className="h-3 w-3 rounded-full border border-border/60 shrink-0"
        style={{ backgroundColor: color || 'hsl(var(--muted-foreground))' }}
      />
      <span className="flex-1 truncate">{label}</span>
      {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// InlineCategoryPicker
// ---------------------------------------------------------------------------

export function InlineCategoryPicker({
  currentPhase,
  pendingCategoryId,
  onPendingCategoryChange,
}: CommonProps) {
  const { canEdit } = usePermissions();
  const canChange = canEdit('kanban');

  const { data, isLoading } = useConversationKanbanOptions(canChange);

  const [open, setOpen] = useState(false);

  const currentCategoryId = currentPhase?.category?.id ?? null;
  const effectiveCategoryId = pendingCategoryId ?? currentCategoryId;

  // Nome a exibir leva em conta o preview (pending)
  const effectiveCategoryName = useMemo(() => {
    if (!effectiveCategoryId) return currentPhase?.category?.name ?? 'Não definida';
    const cat = data?.categoriesWithPhases.find(c => c.id === effectiveCategoryId);
    return cat?.name ?? currentPhase?.category?.name ?? 'Não definida';
  }, [effectiveCategoryId, data, currentPhase]);

  const hasPending = pendingCategoryId !== null && pendingCategoryId !== currentCategoryId;

  /** Troca de categoria = apenas preview local, sem gravar no banco */
  const handleSelect = (cat: CategoryWithPhases) => {
    setOpen(false);
    if (cat.id === currentCategoryId) {
      // Voltou para a categoria original → cancela preview
      onPendingCategoryChange(null);
      return;
    }
    log.debug('Categoria selecionada (preview local)', { categoryId: cat.id });
    onPendingCategoryChange(cat.id);
  };

  if (!canChange) {
    return (
      <div className="flex items-start gap-2">
        <div className="rounded-lg bg-muted p-1.5 shrink-0">
          <Tag className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-muted-foreground">Categoria</p>
          <p className="text-xs font-medium truncate">{effectiveCategoryName}</p>
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-start gap-2 w-full text-left rounded-md -mx-1 px-1 py-0.5',
            'transition-colors hover:bg-muted/60 focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <div className="rounded-lg bg-muted p-1.5 shrink-0">
            <Tag className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-muted-foreground">Categoria</p>
            <p
              className={cn(
                'text-xs font-medium truncate flex items-center gap-1',
                hasPending && 'text-amber-600 dark:text-amber-400',
              )}
            >
              {effectiveCategoryName}
              <ChevronDown className="h-3 w-3 text-muted-foreground/60 shrink-0" />
            </p>
            {hasPending && (
              <p className="text-[10px] text-amber-600 dark:text-amber-400 truncate">
                Selecione uma fase para confirmar
              </p>
            )}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1.5" align="start">
        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Mover para categoria
        </div>
        {isLoading && (
          <div className="px-2 py-3 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Carregando...
          </div>
        )}
        {!isLoading && (!data?.categoriesWithPhases.length) && (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            Nenhuma categoria configurada.
          </div>
        )}
        {!isLoading && !!data && (
          <div className="max-h-72 overflow-y-auto overscroll-contain flex flex-col">
            {data.categoriesWithPhases.map(cat => (
              <PopoverListItem
                key={cat.id}
                color={cat.color}
                label={cat.name}
                isSelected={cat.id === effectiveCategoryId}
                hint={
                  cat.phases.length === 0
                    ? 'Esta categoria não tem fases configuradas'
                    : undefined
                }
                onClick={() => handleSelect(cat)}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// InlinePhasePicker
// ---------------------------------------------------------------------------

export function InlinePhasePicker({
  conversationId,
  contactId,
  inboxId,
  currentPhase,
  pendingCategoryId,
  onPendingCategoryChange,
  onChanged,
}: CommonProps) {
  const { canEdit } = usePermissions();
  const canChange = canEdit('kanban');

  const { data, isLoading } = useConversationKanbanOptions(canChange);
  const { moveConversation } = useKanban();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentPhaseId = currentPhase?.id ?? null;
  const currentPhaseName = currentPhase?.name ?? 'Não definida';
  const currentPhaseColor =
    currentPhase?.color || 'hsl(var(--muted-foreground))';
  const currentCategoryId = currentPhase?.category?.id ?? null;
  const effectiveCategoryId = pendingCategoryId ?? currentCategoryId;

  const hasPending = pendingCategoryId !== null && pendingCategoryId !== currentCategoryId;

  /** Fases da categoria efetiva (pending ou atual) */
  const phasesOfEffectiveCategory = useMemo(() => {
    if (!data || !effectiveCategoryId) return [];
    return (
      data.categoriesWithPhases.find(c => c.id === effectiveCategoryId)?.phases ??
      []
    );
  }, [data, effectiveCategoryId]);

  /** Quando uma categoria pendente é setada, abre automaticamente o popover de fase */
  useEffect(() => {
    if (hasPending && canChange && !saving) {
      setOpen(true);
    }
  }, [hasPending, canChange, saving]);

  const handleSelect = async (phaseId: string, phaseName: string) => {
    // Sem pendência e mesma fase → no-op
    if (!hasPending && phaseId === currentPhaseId) {
      setOpen(false);
      return;
    }

    setOpen(false);
    setSaving(true);
    log.debug('Movendo conversa para nova fase (commit)', {
      conversationId,
      phaseId,
      hasPending,
    });

    const ok = await moveConversation(conversationId, phaseId);
    setSaving(false);

    if (ok) {
      toast.success(`Conversa movida para "${phaseName}"`);
      onPendingCategoryChange(null); // limpa preview
      queryClient.invalidateQueries({
        queryKey: ['contact-sidebar-details', contactId, inboxId, conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-column'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-conversation-counts'] });
      onChanged?.();
    }
  };

  const handleCancelPending = () => {
    onPendingCategoryChange(null);
    setOpen(false);
  };

  // Label e cor da pílula consideram a pendência
  const pillLabel = hasPending ? 'Selecione uma fase' : currentPhaseName;
  const pillColor = hasPending ? 'hsl(var(--muted-foreground))' : currentPhaseColor;

  if (!canChange) {
    return (
      <div className="flex items-start gap-2">
        <div className="rounded-lg bg-muted p-1.5 shrink-0">
          <div
            className="h-3.5 w-3.5 rounded-full border border-border/50"
            style={{ backgroundColor: currentPhaseColor }}
          />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] text-muted-foreground">Fase</p>
          <p className="text-xs font-medium truncate">{currentPhaseName}</p>
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={saving}
          className={cn(
            'flex items-start gap-2 w-full text-left rounded-md -mx-1 px-1 py-0.5',
            'transition-colors hover:bg-muted/60 focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-ring',
            saving && 'opacity-70 cursor-wait',
          )}
        >
          <div className="rounded-lg bg-muted p-1.5 shrink-0">
            <div
              className="h-3.5 w-3.5 rounded-full border border-border/50"
              style={{ backgroundColor: pillColor }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-muted-foreground">Fase</p>
            <p
              className={cn(
                'text-xs font-medium truncate flex items-center gap-1',
                hasPending && 'text-amber-600 dark:text-amber-400',
              )}
            >
              {pillLabel}
              <ChevronDown className="h-3 w-3 text-muted-foreground/60 shrink-0" />
            </p>
          </div>
          {saving && (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground shrink-0" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1.5" align="start">
        <div className="px-2 py-1 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {hasPending ? 'Confirme a nova fase' : 'Mover para fase'}
          </span>
          {hasPending && (
            <button
              type="button"
              onClick={handleCancelPending}
              className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5"
              title="Cancelar mudança de categoria"
            >
              <X className="h-3 w-3" />
              Cancelar
            </button>
          )}
        </div>
        {isLoading && (
          <div className="px-2 py-3 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Carregando...
          </div>
        )}
        {!isLoading && phasesOfEffectiveCategory.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            Nenhuma fase nesta categoria.
          </div>
        )}
        {!isLoading && phasesOfEffectiveCategory.length > 0 && (
          <div className="max-h-72 overflow-y-auto overscroll-contain flex flex-col">
            {phasesOfEffectiveCategory.map(phase => (
              <PopoverListItem
                key={phase.id}
                color={phase.color}
                label={phase.name}
                isSelected={!hasPending && phase.id === currentPhaseId}
                onClick={() => handleSelect(phase.id, phase.name)}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
